import {
  baseApi,
  zodPaginatedResponse,
  zodResponse,
  type Paginated,
} from '@saniplus/api-client';
import {
  ShiftBulkAssignResponseSchema,
  type ShiftListQuerySchema,
  ShiftSchema,
  ShiftSeriesGenerateResponseSchema,
  type ShiftSeriesListQuerySchema,
  ShiftSeriesPreviewResponseSchema,
  ShiftSeriesSchema,
  type Shift,
  type ShiftAssignRequest,
  type ShiftBulkAssignRequest,
  type ShiftCancelRequest,
  type ShiftCreateRequest,
  type ShiftPatchRequest,
  type ShiftSeries,
  type ShiftSeriesCreateRequest,
  type ShiftSeriesGenerateRequest,
  type ShiftSeriesGenerateResponse,
  type ShiftSeriesPatchRequest,
  type ShiftSeriesPreviewRequest,
  type ShiftSeriesPreviewResponse,
} from '@saniplus/contracts';
import type { z } from 'zod';

import { toQueryParams } from '../../shared/api/endpoint';

type ShiftSeriesListArgs = z.input<typeof ShiftSeriesListQuerySchema>;
type ShiftListArgs = z.input<typeof ShiftListQuerySchema>;

/** The list arguments minus `view`, which the calendar endpoint fixes itself. */
type ShiftCalendarArgs = Omit<ShiftListArgs, 'view'>;

/** `PATCH /shift-series/:id` carries its scope in the query string, not the body. */
interface UpdateShiftSeriesArgs {
  readonly id: string;
  readonly body: ShiftSeriesPatchRequest;
  /** `future_only` (the default) leaves already-materialised shifts alone. */
  readonly applyTo?: 'future_only' | 'all';
}

interface GenerateShiftSeriesArgs {
  readonly id: string;
  readonly body: ShiftSeriesGenerateRequest;
}

interface UpdateShiftArgs {
  readonly id: string;
  readonly body: ShiftPatchRequest;
}

interface AssignShiftArgs {
  readonly id: string;
  readonly body: ShiftAssignRequest;
}

interface CancelShiftArgs {
  readonly id: string;
  readonly body: ShiftCancelRequest;
}

/**
 * Scheduling: recurring series and the individual shifts they materialise.
 *
 * The invalidation rule that matters here is that **every series write can move
 * shifts**. `generate` creates and deletes them outright; a patch with
 * `applyTo=all` rewrites the future ones; ending a series stops producing them.
 * Each of those therefore invalidates `Shift` as well as `Series` — a calendar
 * still showing shifts a supervisor just regenerated is the bug this prevents.
 *
 * `preview` is a `POST` that writes nothing (it asks the recurrence engine what
 * a rule *would* produce), so it is a mutation for its imperative trigger and
 * invalidates nothing at all.
 */
export const horairesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ---- series ---------------------------------------------------------
    listShiftSeries: build.query<Paginated<ShiftSeries>, ShiftSeriesListArgs>({
      query: (args) => ({ url: '/shift-series', params: toQueryParams(args) }),
      ...zodPaginatedResponse(ShiftSeriesSchema),
      providesTags: (result) => [
        { type: 'Series' as const, id: 'LIST' },
        ...(result?.items ?? []).map((series) => ({ type: 'Series' as const, id: series.id })),
      ],
    }),

    getShiftSeries: build.query<ShiftSeries, string>({
      query: (id) => ({ url: `/shift-series/${id}` }),
      ...zodResponse(ShiftSeriesSchema),
      providesTags: (_result, _error, id) => [{ type: 'Series', id }],
    }),

    previewShiftSeries: build.mutation<ShiftSeriesPreviewResponse, ShiftSeriesPreviewRequest>({
      query: (body) => ({ url: '/shift-series/preview', method: 'POST', body }),
      ...zodResponse(ShiftSeriesPreviewResponseSchema),
    }),

    createShiftSeries: build.mutation<ShiftSeries, ShiftSeriesCreateRequest>({
      query: (body) => ({ url: '/shift-series', method: 'POST', body }),
      ...zodResponse(ShiftSeriesSchema),
      invalidatesTags: [{ type: 'Series', id: 'LIST' }],
    }),

    updateShiftSeries: build.mutation<ShiftSeries, UpdateShiftSeriesArgs>({
      query: ({ id, body, applyTo }) => ({
        url: `/shift-series/${id}`,
        method: 'PATCH',
        body,
        params: toQueryParams({ applyTo }),
      }),
      ...zodResponse(ShiftSeriesSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Series', id },
        { type: 'Series', id: 'LIST' },
        { type: 'Shift', id: 'LIST' },
      ],
    }),

    generateShiftSeries: build.mutation<ShiftSeriesGenerateResponse, GenerateShiftSeriesArgs>({
      query: ({ id, body }) => ({ url: `/shift-series/${id}/generate`, method: 'POST', body }),
      ...zodResponse(ShiftSeriesGenerateResponseSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Series', id },
        { type: 'Series', id: 'LIST' },
        { type: 'Shift', id: 'LIST' },
      ],
    }),

    pauseShiftSeries: build.mutation<ShiftSeries, string>({
      query: (id) => ({ url: `/shift-series/${id}/pause`, method: 'POST', body: {} }),
      ...zodResponse(ShiftSeriesSchema),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Series', id },
        { type: 'Series', id: 'LIST' },
      ],
    }),

    endShiftSeries: build.mutation<ShiftSeries, string>({
      query: (id) => ({ url: `/shift-series/${id}/end`, method: 'POST', body: {} }),
      ...zodResponse(ShiftSeriesSchema),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Series', id },
        { type: 'Series', id: 'LIST' },
        { type: 'Shift', id: 'LIST' },
      ],
    }),

    // ---- shifts ---------------------------------------------------------
    listShifts: build.query<Paginated<Shift>, ShiftListArgs>({
      query: (args) => ({ url: '/shifts', params: toQueryParams({ view: 'list', ...args }) }),
      ...zodPaginatedResponse(ShiftSchema),
      providesTags: (result) => [
        { type: 'Shift' as const, id: 'LIST' },
        ...(result?.items ?? []).map((shift) => ({ type: 'Shift' as const, id: shift.id })),
      ],
    }),

    /**
     * The same route with `view=calendar`. It is a separate endpoint rather than
     * an argument because the two have separate cache entries and separate
     * lifetimes: a calendar is read by date range and kept while the user
     * navigates weeks, a list is read by filter and thrown away on every change.
     */
    listCalendarShifts: build.query<Paginated<Shift>, ShiftCalendarArgs>({
      query: (args) => ({ url: '/shifts', params: toQueryParams({ ...args, view: 'calendar' }) }),
      ...zodPaginatedResponse(ShiftSchema),
      providesTags: (result) => [
        { type: 'Shift' as const, id: 'LIST' },
        ...(result?.items ?? []).map((shift) => ({ type: 'Shift' as const, id: shift.id })),
      ],
    }),

    getShift: build.query<Shift, string>({
      query: (id) => ({ url: `/shifts/${id}` }),
      ...zodResponse(ShiftSchema),
      providesTags: (_result, _error, id) => [{ type: 'Shift', id }],
    }),

    createShift: build.mutation<Shift, ShiftCreateRequest>({
      query: (body) => ({ url: '/shifts', method: 'POST', body }),
      ...zodResponse(ShiftSchema),
      invalidatesTags: [{ type: 'Shift', id: 'LIST' }],
    }),

    updateShift: build.mutation<Shift, UpdateShiftArgs>({
      query: ({ id, body }) => ({ url: `/shifts/${id}`, method: 'PATCH', body }),
      ...zodResponse(ShiftSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Shift', id },
        { type: 'Shift', id: 'LIST' },
      ],
    }),

    assignShift: build.mutation<Shift, AssignShiftArgs>({
      query: ({ id, body }) => ({ url: `/shifts/${id}/assign`, method: 'POST', body }),
      ...zodResponse(ShiftSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Shift', id },
        { type: 'Shift', id: 'LIST' },
      ],
    }),

    cancelShift: build.mutation<Shift, CancelShiftArgs>({
      query: ({ id, body }) => ({ url: `/shifts/${id}/cancel`, method: 'POST', body }),
      ...zodResponse(ShiftSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Shift', id },
        { type: 'Shift', id: 'LIST' },
      ],
    }),

    bulkAssignShifts: build.mutation<Shift[], ShiftBulkAssignRequest>({
      query: (body) => ({ url: '/shifts/bulk-assign', method: 'POST', body }),
      ...zodResponse(ShiftBulkAssignResponseSchema),
      invalidatesTags: (_result, _error, { shiftIds }) => [
        { type: 'Shift' as const, id: 'LIST' },
        ...shiftIds.map((id) => ({ type: 'Shift' as const, id })),
      ],
    }),
  }),
});

export const {
  useListShiftSeriesQuery,
  useLazyListShiftSeriesQuery,
  useGetShiftSeriesQuery,
  usePreviewShiftSeriesMutation,
  useCreateShiftSeriesMutation,
  useUpdateShiftSeriesMutation,
  useGenerateShiftSeriesMutation,
  usePauseShiftSeriesMutation,
  useEndShiftSeriesMutation,
  useListShiftsQuery,
  useLazyListShiftsQuery,
  useListCalendarShiftsQuery,
  useGetShiftQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useAssignShiftMutation,
  useCancelShiftMutation,
  useBulkAssignShiftsMutation,
} = horairesApi;
